import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Trash2, Edit, Copy, Power, Stethoscope, GitBranch, ListTree,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import ProtocolEditor from "@/pages/protocols/ProtocolEditor";

function ProtocolList({ onEdit, onCreate }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [categories, setCategories] = useState([]);
  const [confirm, setConfirm] = useState({ open: false });
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/protocols");
      setItems(r.data.data ?? []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    load();
    (async () => {
      try {
        const cats = await api.get("/lookups/categories", { params: { per_page: 200 } });
        const pc = (cats.data.data ?? []).find((c) => c.code === "protocol_categories");
        if (pc) {
          const r = await api.get("/lookups/values", { params: { category_id: pc.id } });
          setCategories(r.data.data ?? []);
        }
      } catch {}
    })();
  }, []);

  const filtered = useMemo(() => items.filter((p) => {
    if (search && !(`${p.name} ${p.code}`).toLowerCase().includes(search.toLowerCase())) return false;
    if (categoryFilter !== "__all__" && p.category_code !== categoryFilter) return false;
    if (statusFilter !== "__all__" && p.status !== statusFilter) return false;
    return true;
  }), [items, search, categoryFilter, statusFilter]);

  async function duplicate(p) {
    try { await api.post(`/protocols/${p.id}/duplicate`); toast.success("Protocol duplicated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function toggleStatus(p) {
    try { await api.post(`/protocols/${p.id}/${p.status === "active" ? "deactivate" : "activate"}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove(p) {
    try { await api.delete(`/protocols/${p.id}`); toast.success("Protocol deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="protocols-page">
      <PageHeader
        title="Clinical Protocols"
        subtitle="Configurable decision trees that nurses follow during hotline triage."
        actions={
          <Button onClick={onCreate} data-testid="create-protocol-button" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
            <Plus className="w-4 h-4 mr-1.5" /> New Protocol
          </Button>
        }
      />

      <div className="hah-card p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg flex-1 min-w-[220px]">
          <Stethoscope className="w-4 h-4 text-[#94A3B8]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or code…" className="bg-transparent outline-none text-sm w-full" data-testid="protocol-search" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-44" data-testid="protocol-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-12 text-center text-sm text-[#6B7280]">
          <ListTree className="w-10 h-10 mx-auto text-[#94A3B8] mb-3" />
          No protocols yet. Click <strong>New Protocol</strong> to build the first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <div key={p.id} className="hah-card p-4 flex flex-col gap-2 hover:shadow-md transition-shadow" data-testid={`protocol-card-${p.code}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">{categories.find((c) => c.value === p.category_code)?.label || p.category_code || "Uncategorised"}</div>
                  <h3 className="text-sm font-semibold text-[#1F2937] mt-0.5 truncate">{p.name}</h3>
                  <div className="text-xs text-[#94A3B8] mt-0.5 font-mono">{p.code} · v{p.version}</div>
                </div>
                <StatusBadge status={p.status} />
              </div>
              <p className="text-sm text-[#6B7280] line-clamp-2">{p.description || "No description"}</p>
              <div className="flex items-center gap-3 text-xs text-[#6B7280] pt-1 border-t border-[#E5E7EB] mt-1">
                <span className="flex items-center gap-1"><GitBranch className="w-3.5 h-3.5" /> {p.node_count} nodes</span>
              </div>
              <div className="flex items-center justify-end gap-1.5 mt-1">
                <Button size="sm" variant="outline" className="h-8 border-[#E5E7EB]" onClick={() => onEdit(p.id)} data-testid={`protocol-edit-${p.code}`}>
                  <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="h-8 border-[#E5E7EB]" onClick={() => duplicate(p)} data-testid={`protocol-dup-${p.code}`}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="h-8 border-[#E5E7EB]" onClick={() => toggleStatus(p)} data-testid={`protocol-toggle-${p.code}`}>
                  <Power className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="h-8 border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412]"
                  onClick={() => setConfirm({ open: true, title: "Delete protocol", description: `Delete "${p.name}"? This cannot be undone.`, destructive: true, onConfirm: () => remove(p) })}
                  data-testid={`protocol-del-${p.code}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog open={confirm.open} onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title} description={confirm.description} destructive={confirm.destructive}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }} />
    </div>
  );
}

export default function ProtocolsPage() {
  const [view, setView] = useState({ mode: "list" });

  if (view.mode === "edit") {
    return <ProtocolEditor protocolId={view.id} onBack={(newId) => setView(newId ? { mode: "edit", id: newId } : { mode: "list" })} />;
  }
  if (view.mode === "new") {
    return <ProtocolEditor protocolId={null} onBack={(newId) => setView(newId ? { mode: "edit", id: newId } : { mode: "list" })} />;
  }
  return <ProtocolList onEdit={(id) => setView({ mode: "edit", id })} onCreate={() => setView({ mode: "new" })} />;
}
